import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Search, ArrowLeft, Globe, Scale } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

// Language flag mapping
const languageFlags = {
  at: '🇦🇹',
  de: '🇩🇪',
  en: '🇬🇧',
  bg: '🇧🇬',
  cz: '🇨🇿',
  hr: '🇭🇷',
  hu: '🇭🇺',
  ro: '🇷🇴',
  pl: '🇵🇱',
  fr: '🇫🇷',
  it: '🇮🇹',
  zh: '🇨🇳',
  ch: '🇨🇭',
  eu: '🇪🇺',
  es: '🇪🇸',
  ru: '🇷🇺',
  sk: '🇸🇰',
  sl: '🇸🇮',
  sr: '🇷🇸',
  tr: '🇹🇷',
  uk: '🇺🇦'
};

// Language display names
const languageNames = {
  at: { de: 'Deutsch (AT)', en: 'German (AT)' },
  de: { de: 'Deutsch (DE)', en: 'German (DE)' },
  en: { de: 'Englisch', en: 'English' },
  bg: { de: 'Bulgarisch', en: 'Bulgarian' },
  cz: { de: 'Tschechisch', en: 'Czech' },
  hr: { de: 'Kroatisch', en: 'Croatian' },
  hu: { de: 'Ungarisch', en: 'Hungarian' },
  ro: { de: 'Rumänisch', en: 'Romanian' },
  pl: { de: 'Polnisch', en: 'Polish' },
  fr: { de: 'Französisch', en: 'French' },
  it: { de: 'Italienisch', en: 'Italian' },
  zh: { de: 'Chinesisch', en: 'Chinese' },
  es: { de: 'Spanisch', en: 'Spanish' },
  ru: { de: 'Russisch', en: 'Russian' },
  sk: { de: 'Slowakisch', en: 'Slovak' },
  sl: { de: 'Slowenisch', en: 'Slovenian' },
  sr: { de: 'Serbisch', en: 'Serbian' },
  tr: { de: 'Türkisch', en: 'Turkish' },
  uk: { de: 'Ukrainisch', en: 'Ukrainian' }
};

// EU member country codes (used for legal context filter logic)
const EU_COUNTRIES = ['at', 'de', 'bg', 'hr', 'cz', 'fr', 'hu', 'it', 'pl', 'ro', 'sk', 'sl', 'es'];

// Legal context names
const legalContextNames = {
  at: { de: 'Österreich', en: 'Austria' },
  de: { de: 'Deutschland', en: 'Germany' },
  eu: { de: 'EU', en: 'EU' },
  global: { de: 'Global', en: 'Global' },
};

export default function ProduktKatalogPage() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [elearnings, setElearnings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeLanguage, setActiveLanguage] = useState('all');
  const [activeLegalContext, setActiveLegalContext] = useState('all');

  // Read category from URL params on mount
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setActiveCategory(categoryParam);
    }
  }, [searchParams, setActiveCategory]);

  const scrollToFilters = () => {
    const el = document.getElementById('catalog-filters');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };
  const setCategory = (v) => { setActiveCategory(v); scrollToFilters(); };
  const setLanguageFilter = (v) => { setActiveLanguage(v); scrollToFilters(); };
  const setLegalFilter = (v) => { setActiveLegalContext(v); scrollToFilters(); };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [elearningsRes, categoriesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/elearnings`),
          axios.get(`${BACKEND_URL}/api/categories`)
        ]);
        setElearnings(elearningsRes.data.elearnings);
        setCategories(categoriesRes.data.categories);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch catalog data:', error);
        }
        setElearnings([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // BACKEND_URL is constant, axios is stable, setters are stable // eslint-disable-line react-hooks/exhaustive-deps

  // Get all unique languages and legal contexts from courses
  const allLanguages = [...new Set(elearnings.flatMap(e => e.languages || []))].sort();
  const allLegalContexts = [...new Set(elearnings.flatMap(e => e.legalContext || []))].sort();

  // Filter by search, category, language and legal context
  const filteredElearnings = elearnings.filter((item) => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesLanguage = activeLanguage === 'all' || item.languages?.includes(activeLanguage);
    const matchesLegal = activeLegalContext === 'all' || (() => {
      const contexts = item.legalContext || [];
      // Direct match
      if (contexts.includes(activeLegalContext)) return true;
      // EU country selected → also show EU and Global items
      if (EU_COUNTRIES.includes(activeLegalContext)) {
        return contexts.includes('eu') || contexts.includes('global');
      }
      // "eu" selected → also show Global items
      if (activeLegalContext === 'eu') {
        return contexts.includes('global');
      }
      // Non-EU country selected → also show Global items
      if (activeLegalContext !== 'global') {
        return contexts.includes('global');
      }
      return false;
    })();
    const query = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      item.title?.de?.toLowerCase().includes(query) ||
      item.title?.en?.toLowerCase().includes(query) ||
      item.description?.de?.toLowerCase().includes(query) ||
      item.description?.en?.toLowerCase().includes(query);
    return matchesCategory && matchesLanguage && matchesLegal && matchesSearch;
  });

  // Group by category for display
  const groupedByCategory = {};
  filteredElearnings.forEach(item => {
    if (!groupedByCategory[item.category]) {
      groupedByCategory[item.category] = [];
    }
    groupedByCategory[item.category].push(item);
  });

  // Get category name
  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name[language] : categoryId;
  };

  return (
    <div className="min-h-screen pt-20 bg-[#E7F0FA]" data-testid="produktkatalog-page">
      <SEO
        title={language === 'de' ? 'E-Learning Produktkatalog - Arbeitssicherheit & Compliance Module' : 'E-Learning Product Catalog - Workplace Safety & Compliance Modules'}
        description={language === 'de' 
          ? 'Alle E-Learning Module im Überblick: Arbeitssicherheit, Brandschutz, Erste Hilfe, Cyber Security, DSGVO, Hygiene und mehr. SCORM-kompatibel, mehrsprachig, rechtssicher.' 
          : 'All e-learning modules at a glance: Workplace safety, fire protection, first aid, cyber security, GDPR, hygiene and more. SCORM-compatible, multilingual, legally compliant.'}
        keywords={language === 'de'
          ? 'E-Learning Module, Arbeitssicherheit Schulung, Brandschutz E-Learning, Erste Hilfe Unterweisung, Cyber Security Training, DSGVO Schulung, Hygiene E-Learning, SCORM Module, Pflichtschulungen online, Arbeitsschutz E-Learning'
          : 'E-Learning Modules, Workplace Safety Training, Fire Protection E-Learning, First Aid Instruction, Cyber Security Training, GDPR Training, Hygiene E-Learning, SCORM Modules, Mandatory Training Online'}
        path="/produktkatalog"
        lang={language}
      />
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#E7F0FA] via-white to-[#dde9f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/solutions" 
            className="inline-flex items-center text-[#2E5E99] mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'de' ? 'Zurück zu Lösungen' : 'Back to Solutions'}
          </Link>
          
          <motion.div {...fadeUp}>
            <h1 className="text-3xl md:text-5xl font-bold text-[#0D2440] mb-4">
              {language === 'de' ? 'Produktkatalog' : 'Product Catalog'}
            </h1>
            <p className="text-lg text-[#5A7A9B] max-w-2xl">
              {language === 'de'
                ? 'Entdecken Sie alle unsere standardisierten E-Learning-Module – sofort einsatzbereit und rechtssicher.'
                : 'Discover all our standardized e-learning modules – ready to use and legally compliant.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section id="catalog-filters" className="py-6 bg-white border-b border-[#C8D8E8] sticky top-20 z-40 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4">
            {/* Category Pills - Top Row */}
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              <button
                onClick={() => setCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === 'all'
                    ? 'bg-[#0D2440] text-white'
                    : 'bg-[#E7F0FA] text-[#5A7A9B] hover:bg-[#C8D8E8]'
                }`}
              >
                {language === 'de' ? 'Alle' : 'All'} ({elearnings.length})
              </button>
              {categories.map((cat) => {
                const count = elearnings.filter(e => e.category === cat.id).length;
                if (count === 0) return null;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setCategory(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeCategory === cat.id
                        ? 'bg-[#0D2440] text-white'
                        : 'bg-[#E7F0FA] text-[#5A7A9B] hover:bg-[#C8D8E8]'
                    }`}
                  >
                    {cat.name[language]} ({count})
                  </button>
                );
              })}
            </div>

            {/* Language + Legal Context + Search - Bottom Row */}
            <div className="flex flex-col md:flex-row md:flex-wrap items-start md:items-center gap-3 justify-center md:justify-start">
              {/* Language Filter */}
              <div className="w-full md:w-auto overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
                <div className="flex items-center gap-1 bg-[#E7F0FA] rounded-full p-1 w-max" data-testid="language-filter">
                  <button
                    onClick={() => setLanguageFilter('all')}
                    className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      activeLanguage === 'all'
                        ? 'bg-[#0D2440] text-white'
                        : 'text-[#5A7A9B] hover:bg-[#C8D8E8]'
                    }`}
                    data-testid="lang-filter-all"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    <span>{language === 'de' ? 'Alle' : 'All'}</span>
                  </button>
                  {allLanguages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setLanguageFilter(lang)}
                      className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                        activeLanguage === lang
                          ? 'bg-[#0D2440] text-white'
                          : 'text-[#5A7A9B] hover:bg-[#C8D8E8]'
                      }`}
                      data-testid={`lang-filter-${lang}`}
                    >
                      <span>{languageFlags[lang]}</span>
                      <span>{lang.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Legal Context Filter */}
              {allLegalContexts.length > 0 && (
                <div className="w-full md:w-auto overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
                  <div className="flex items-center gap-1 bg-[#E7F0FA] rounded-full p-1 w-max" data-testid="legal-context-filter">
                    <button
                      onClick={() => setLegalFilter('all')}
                      className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                        activeLegalContext === 'all'
                          ? 'bg-[#0D2440] text-white'
                          : 'text-[#5A7A9B] hover:bg-[#C8D8E8]'
                      }`}
                      data-testid="legal-filter-all"
                    >
                      <Scale className="w-3.5 h-3.5" />
                      <span>{language === 'de' ? 'Alle' : 'All'}</span>
                    </button>
                    {allLegalContexts.map((ctx) => (
                      <button
                        key={ctx}
                        onClick={() => setLegalFilter(ctx)}
                        className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                          activeLegalContext === ctx
                            ? 'bg-[#0D2440] text-white'
                            : 'text-[#5A7A9B] hover:bg-[#C8D8E8]'
                        }`}
                        data-testid={`legal-filter-${ctx}`}
                      >
                        {ctx === 'global' ? (
                          <Globe className="w-3.5 h-3.5" />
                        ) : (
                          <span>{languageFlags[ctx]}</span>
                        )}
                        <span>{legalContextNames[ctx]?.[language] || ctx.toUpperCase()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A7A9B]" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'de' ? 'Suchen...' : 'Search...'}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legend */}
      <section className="pt-8 pb-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-2 text-sm text-[#5A7A9B]" data-testid="legal-context-legend">
            <Scale className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#2E5E99]" />
            <p>
              <span className="font-medium text-[#0D2440]">
                {language === 'de' ? 'Rechtskontext' : 'Legal Context'}:
              </span>{' '}
              {language === 'de'
                ? 'Gemäß den gesetzlichen Bestimmungen der aufgeführten Länder.'
                : 'According to the law of the listed countries.'}
            </p>
          </div>
        </div>
      </section>

      {/* Catalog Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-[#2E5E99] border-t-transparent rounded-full mx-auto" />
            </div>
          ) : (
            <>
              {activeCategory === 'all' ? (
                // Show grouped by category
                Object.keys(groupedByCategory).map((categoryId) => (
                  <div key={categoryId} className="mb-16">
                    <h2 className="text-2xl font-bold text-[#0D2440] mb-6 pb-3 border-b border-[#C8D8E8]">
                      {getCategoryName(categoryId)}
                      <span className="text-sm font-normal text-[#5A7A9B] ml-3">
                        ({groupedByCategory[categoryId].length} {language === 'de' ? 'Module' : 'modules'})
                      </span>
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {groupedByCategory[categoryId].map((item, index) => (
                        <CourseCard key={item.id} item={item} language={language} index={index} />
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                // Show filtered results
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {filteredElearnings.map((item, index) => (
                    <CourseCard key={item.id} item={item} language={language} index={index} />
                  ))}
                </div>
              )}

              {filteredElearnings.length === 0 && !loading && (
                <div className="text-center py-20">
                  <p className="text-[#5A7A9B] text-lg">
                    {language === 'de' ? 'Keine Module gefunden.' : 'No modules found.'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0D2440]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {language === 'de' ? 'Individuelle Anforderungen?' : 'Custom Requirements?'}
          </h2>
          <p className="text-white/70 mb-8">
            {language === 'de'
              ? 'Wir entwickeln auch maßgeschneiderte E-Learning-Module für Ihre spezifischen Bedürfnisse.'
              : 'We also develop custom e-learning modules for your specific needs.'}
          </p>
          <Link to="/booking">
            <Button className="bg-[#2E5E99] hover:bg-[#1E4F85] text-white h-12 px-8">
              {language === 'de' ? 'Demo / Anfrage' : 'Demo / Inquiry'}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

// Course Card Component
function CourseCard({ item, language, index }) {
  return (
    <motion.div
      data-testid={`course-card-${item.id}`}
      className="group bg-white rounded-xl overflow-hidden border border-[#C8D8E8] hover:shadow-xl transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: Math.min(index * 0.05, 0.3) }}
    >
      <Link to={`/course/${item.id}`} className="block aspect-square overflow-hidden cursor-pointer">
        <img
          src={item.image}
          alt={item.title[language]}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </Link>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-0.5" title={language === 'de' ? 'Sprachen' : 'Languages'}>
            {item.languages?.map((lang) => (
              <span key={lang} className="text-xs" title={languageNames[lang]?.[language] || lang.toUpperCase()}>
                {languageFlags[lang]}
              </span>
            ))}
          </div>
          {item.legalContext && (
            <div className="flex items-center space-x-1" title={language === 'de' ? 'Rechtskontext' : 'Legal Context'}>
              <Scale className="w-3 h-3 text-[#5A7A9B]" />
              {item.legalContext.map((ctx) => (
                ctx === 'global' ? (
                  <Globe key={ctx} className="w-3.5 h-3.5 text-[#2E5E99]" />
                ) : (
                  <span key={ctx} className="text-xs" title={legalContextNames[ctx]?.[language] || ctx.toUpperCase()}>
                    {languageFlags[ctx]}
                  </span>
                )
              ))}
            </div>
          )}
        </div>
        <h3 className="font-semibold text-[#0D2440] mb-2 line-clamp-2 min-h-[48px]">
          {item.title[language]}
        </h3>
        <p className="text-sm text-[#5A7A9B] mb-3 line-clamp-2">
          {item.description[language]}
        </p>
        <div className="flex flex-col gap-2">
          <Link
            to={`/course/${item.id}`}
            className={`text-sm font-medium inline-flex items-center justify-center transition-colors ${
              item.demoUrl
                ? 'text-white bg-[#2E5E99] hover:bg-[#1E4F85] px-3 py-1.5 rounded-lg'
                : 'text-[#2E5E99] hover:underline'
            }`}
            data-testid={`course-link-${item.id}`}
          >
            {item.demoUrl
              ? (language === 'de' ? 'Kursdetails und Demo' : 'Course Details & Demo')
              : (language === 'de' ? 'Mehr erfahren' : 'Learn More')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
          {item.hasDetailPage && (
            <Link
              to="/visitor-system"
              className="text-sm font-medium inline-flex items-center justify-center text-white bg-cyan-600 hover:bg-cyan-500 px-3 py-1.5 rounded-lg transition-colors"
              data-testid={`detail-page-link-${item.id}`}
            >
              {language === 'de' ? 'Systemdetails ansehen' : 'View System Details'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}
